import { ServiceEndpoints } from "../../../../constants/ServiceEndpoints";

declare global {
  interface Window {
    __nineGoogleMapsReady?: () => void;
  }
}

let mapsLoadPromise: Promise<void> | null = null;
let mapsLoadKey: string | null = null;

/**
 * With loading=async, the script's DOM "load" event fires before Google
 * finishes initializing google.maps.importLibrary — must wait on their
 * callback param instead, which only fires once the API is fully ready.
 *
 * The script is shared across every caller (PlaceSearch, LocationPinPicker,
 * ShareProfileModal, ...): whichever call arrives first decides the apiKey
 * for the page's lifetime — later callers just await the same load.
 */
export const loadGoogleMapsAPI = (apiKey: string, onLoad: () => void): Promise<void> => {
  if (typeof window.google?.maps?.importLibrary === "function") {
    onLoad();
    return Promise.resolve();
  }

  if (mapsLoadPromise && mapsLoadKey !== apiKey) {
    console.warn(
      "loadGoogleMapsAPI: ignoring apiKey — Maps already loading/loaded with a different key.",
    );
  }

  if (!mapsLoadPromise) {
    mapsLoadKey = apiKey;
    mapsLoadPromise = new Promise((resolve, reject) => {
      window.__nineGoogleMapsReady = () => {
        delete window.__nineGoogleMapsReady;
        resolve();
      };

      const script = document.createElement("script");
      script.src = `${ServiceEndpoints.GOOGLE_MAPS_API_BASE}/maps/api/js?key=${apiKey}&libraries=places&loading=async&callback=__nineGoogleMapsReady`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        delete window.__nineGoogleMapsReady;
        script.remove();
        mapsLoadPromise = null;
        mapsLoadKey = null;
        reject(new Error("Failed to load Google Maps API"));
      };
      document.body.appendChild(script);
    });
  }

  // Preserve callback callers while also allowing newer callers to handle errors.
  const ready = mapsLoadPromise.then(onLoad);
  void ready.catch((err) => console.error(err));
  return ready;
};

/** Creates a new Autocomplete session token */
export const createSessionToken = () => {
  if (!window.google?.maps) throw new Error("Google Maps not loaded");
  return new google.maps.places.AutocompleteSessionToken();
};

/** Fetches autocomplete suggestions */
export const fetchPlaceSuggestions = async (
  input: string,
  sessionToken: google.maps.places.AutocompleteSessionToken,
) => {
  const { AutocompleteSuggestion } = (await google.maps.importLibrary(
    "places",
  )) as google.maps.PlacesLibrary;

  const request: google.maps.places.AutocompleteRequest = {
    input,
    sessionToken,
    language: "en-IN",
  };

  const { suggestions } =
    await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

  return suggestions.flatMap(({ placePrediction }) => placePrediction ? [{
    text: placePrediction.text.toString(),
    place: placePrediction.toPlace(),
  }] : []);
};

/** Fetches details of a selected place */
export const fetchPlaceDetails = async (place: google.maps.places.Place) => {
  await place.fetchFields({ fields: ["displayName", "formattedAddress"] });
  return {
    name: place.displayName || "Unknown Place",
    addr: place.formattedAddress || "",
  };
};

/**
 * Forward geocodes a free-text address into GPS coordinates.
 * Resolves to null (rather than rejecting) on failure so callers can
 * fall back to another center source without try/catch noise.
 */
export const geocodeAddress = (
  address: string,
): Promise<{ lat: number; lng: number } | null> => {
  if (!window.google?.maps || !address.trim()) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      { address },
      (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        if (status !== google.maps.GeocoderStatus.OK || !results || results.length === 0) {
          return resolve(null);
        }
        const location = results[0].geometry.location;
        resolve({ lat: location.lat(), lng: location.lng() });
      },
    );
  });
};

/**
 * Reverse geocodes GPS coordinates into a human-readable location name.
 * Prioritises the most specific meaningful area:
 *   sublocality_level_1 → locality → administrative_area_level_2
 * Falls back to the first result's formatted_address if no match.
 */
export const reverseGeocode = (
  lat: number,
  lng: number,
): Promise<{ displayName: string; formattedAddress: string }> => {
  if (!window.google?.maps) {
    return Promise.reject(new Error("Google Maps API not loaded"));
  }

  return new Promise((resolve, reject) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      { location: { lat, lng } },
      (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        if (status !== google.maps.GeocoderStatus.OK || !results || results.length === 0) {
          return reject(new Error(`Geocoding failed with status: ${status}`));
        }

        // Walk through results to find the best area-level name
        // Priority: most specific → least specific
        const targetTypes = [
          "neighborhood",
          "sublocality_level_2",
          "sublocality_level_1",
          "sublocality",
          "route",
          "locality",
          "administrative_area_level_2",
        ];

        for (const targetType of targetTypes) {
          const match = results.find((r) => r.types.includes(targetType));
          if (match) {
            // Use the component's long_name for a clean display
            const component = match.address_components.find((c) =>
              c.types.includes(targetType),
            );
            return resolve({
              displayName: component?.long_name || match.formatted_address,
              formattedAddress: match.formatted_address,
            });
          }
        }

        // Fallback: use the first result's formatted address
        resolve({
          displayName: results[0].formatted_address,
          formattedAddress: results[0].formatted_address,
        });
      },
    );
  });
};

/**
 * Like reverseGeocode but returns a higher-level area name (city / district).
 * Priority: locality → administrative_area_level_2 → administrative_area_level_1
 * Falls back to the first result's formatted_address.
 */
export const reverseGeocodeCity = (
  lat: number,
  lng: number,
): Promise<{ displayName: string; formattedAddress: string }> => {
  if (!window.google?.maps) {
    return Promise.reject(new Error("Google Maps API not loaded"));
  }

  return new Promise((resolve, reject) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      { location: { lat, lng } },
      (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        if (status !== google.maps.GeocoderStatus.OK || !results || results.length === 0) {
          return reject(new Error(`Geocoding failed: ${status}`));
        }

        const cityTypes = [
          "locality",
          "administrative_area_level_2",
          "administrative_area_level_1",
        ];

        for (const targetType of cityTypes) {
          const match = results.find((r) => r.types.includes(targetType));
          if (match) {
            const component = match.address_components.find((c) =>
              c.types.includes(targetType),
            );
            return resolve({
              displayName: component?.long_name || match.formatted_address,
              formattedAddress: match.formatted_address,
            });
          }
        }

        resolve({
          displayName: results[0].formatted_address,
          formattedAddress: results[0].formatted_address,
        });
      },
    );
  });
};
